import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { inboxApi } from '../services/api';
import { useAuth } from './AuthContext';

const InboxContext = createContext({ pendingCount: 0, refresh: () => {} });

export const useInbox = () => useContext(InboxContext);

const POLL_INTERVAL_MS = 60 * 1000; // 60 secondi

export const InboxProvider = ({ children }) => {
  const { user } = useAuth();
  const [pendingCount, setPendingCount] = useState(0);
  const timerRef = useRef(null);
  const prevCountRef = useRef(null); // null = primo load, non suonare

  const isCompanyUser = !!user && user.role !== 'super_admin' && !!user.company_id;

  /**
   * Breve "ding" bitonale via Web Audio API — nessun file asset, zero richieste di rete.
   * Alcuni browser (Safari iOS) richiedono un'interazione utente prima di consentire audio:
   * il primo polling può essere silenzioso ma dal secondo in poi funziona.
   */
  const playNotificationSound = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.18, now + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.45);
      gain.connect(ctx.destination);
      const osc1 = ctx.createOscillator();
      osc1.type = 'sine';
      osc1.frequency.setValueAtTime(880, now);        // A5
      osc1.frequency.setValueAtTime(1318.5, now + 0.12); // E6
      osc1.connect(gain);
      osc1.start(now);
      osc1.stop(now + 0.45);
      // Chiudi il contesto dopo per non lasciare risorse appese
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 600);
    } catch { /* silent — audio è best-effort */ }
  };

  const refresh = useCallback(async () => {
    if (!isCompanyUser) return;
    try {
      const r = await inboxApi.list('pending');
      const count = typeof r.data?.pending_count === 'number'
        ? r.data.pending_count
        : (r.data?.items || []).length;
      const prev = prevCountRef.current;
      setPendingCount(count);
      // Suona SOLO se il contatore è aumentato E non è il primissimo load
      if (prev !== null && count > prev) {
        playNotificationSound();
      }
      prevCountRef.current = count;
    } catch (e) {
      // Silenzioso in produzione (la rete può essere intermittente — PWA offline).
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[Inbox] polling failed:', e?.response?.status || e?.message);
      }
    }
  }, [isCompanyUser]);

  useEffect(() => {
    if (!isCompanyUser) {
      setPendingCount(0);
      prevCountRef.current = null;
      return;
    }
    refresh();
    timerRef.current = setInterval(refresh, POLL_INTERVAL_MS);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isCompanyUser, refresh]);

  return (
    <InboxContext.Provider value={{ pendingCount, refresh }}>
      {children}
    </InboxContext.Provider>
  );
};
